import { apiJson } from '@/api/client'
import type { TransactionsListResponse } from '@/features/transactions/types'
import type {
  AccountDetail,
  Customer360Aggregate,
  CustomerAccount,
  CustomerAlertOrCase,
  CustomerBlock,
  CustomerNetworkResponse,
  CustomerNote,
  CustomerRelatedParty,
  DossierExportJob,
  FlagSuspiciousPayload,
  TimelineEntry,
} from './types'

export function get360(customerId: string) {
  return apiJson<Customer360Aggregate>(`/customers/${customerId}/360`)
}

export function listRelatedParties(customerId: string) {
  return apiJson<CustomerRelatedParty[]>(
    `/customers/${customerId}/related-parties`,
  )
}

export function listAccounts(customerId: string) {
  return apiJson<{ accounts: CustomerAccount[] }>(
    `/customers/${customerId}/accounts`,
  )
}

export interface CustomerTransactionFilters {
  q?: string
  channel?: string[]
  status?: string[]
  amount_min_minor?: number
  amount_max_minor?: number
  /** Default 30 days lookback when undefined. */
  days?: number
}

export function listTransactions(
  customerId: string,
  filters: CustomerTransactionFilters = {},
) {
  const sp = new URLSearchParams()
  for (const c of filters.channel ?? []) sp.append('channel', c)
  for (const s of filters.status ?? []) sp.append('status', s)
  if (filters.q) sp.set('q', filters.q)
  if (filters.amount_min_minor !== undefined)
    sp.set('amount_min_minor', String(filters.amount_min_minor))
  if (filters.amount_max_minor !== undefined)
    sp.set('amount_max_minor', String(filters.amount_max_minor))
  if (filters.days !== undefined) sp.set('days', String(filters.days))
  const s = sp.toString()
  return apiJson<TransactionsListResponse>(
    `/customers/${customerId}/transactions${s ? `?${s}` : ''}`,
  )
}

export function listAlertsAndCases(customerId: string) {
  return apiJson<{ items: CustomerAlertOrCase[] }>(
    `/customers/${customerId}/alerts-and-cases`,
  )
}

export function listBlocks(customerId: string) {
  return apiJson<{ blocks: CustomerBlock[] }>(`/customers/${customerId}/blocks`)
}

export function listNotes(customerId: string) {
  return apiJson<{ notes: CustomerNote[] }>(`/customers/${customerId}/notes`)
}

export function listTimeline(customerId: string) {
  return apiJson<{ entries: TimelineEntry[] }>(
    `/customers/${customerId}/timeline`,
  )
}

export function getNetwork(customerId: string, nodeLimit: number) {
  return apiJson<CustomerNetworkResponse>(
    `/customers/${customerId}/network?node_limit=${nodeLimit}`,
  )
}

export function startDossierExport(customerId: string) {
  return apiJson<DossierExportJob>(`/customers/${customerId}/dossier`, {
    method: 'POST',
  })
}

export function getDossierJob(customerId: string, jobId: string) {
  return apiJson<DossierExportJob>(`/customers/${customerId}/dossier/${jobId}`)
}

export function getAccountDetail(customerId: string, nuban: string) {
  return apiJson<AccountDetail>(`/customers/${customerId}/accounts/${nuban}`)
}

export function flagAccountSuspicious(payload: FlagSuspiciousPayload) {
  return apiJson<{ alert_id: string; alert_number: string }>(
    `/customers/${payload.customer_id}/accounts/${payload.nuban}/flag-suspicious`,
    { method: 'POST', body: { reason: payload.reason } },
  )
}
